/**
 * Der anonyme Aushang, druckfertig.
 *
 * Bisher gab es nur eine CSV — die musste jemand von Hand in Excel formatieren,
 * bevor sie ans Brett konnte. Die Vorlage ist eine einseitige Übersicht: eine
 * Zeile je Stempeluhr-ID, eine Spalte je Betriebstag, Summe je Halbmonat.
 *
 * Bewusst kein serverseitiges PDF: der Browser druckt diese Seite direkt oder
 * sichert sie als PDF. Was auf dem Bildschirm steht, kommt aufs Papier, und es
 * braucht keine zweite Rendering-Bibliothek, die irgendwann anders aussieht als
 * die Anwendung.
 *
 * Zwei Fassungen, weil zwei Zwecke:
 *
 * - **Nur Stempeluhr-IDs** (Spec 2.12). Das Blatt hängt
 *   öffentlich; wer wie viel Trinkgeld bekommt, geht die Gäste nichts an.
 * - **Mit Namen** — so hängt der Betrieb es tatsächlich aus. Früher trug
 *   diese Fassung „Nicht zum Aushängen" in Kopf und Fußzeile; das stimmte
 *   für den Betrieb nicht und stand deshalb falsch auf jedem Ausdruck.
 *
 * Voreingestellt bleibt die anonyme Fassung (Spec 2.12).
 *
 * Ausgehängt wird je Monatshälfte (`haelfte=1|2`): dann stehen nur deren
 * Tage und eine Summe da, ohne leere Spalten der anderen Hälfte. Ohne den
 * Parameter zeigt das Blatt den ganzen Monat wie die Monatsübersicht.
 */
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type WorkflowDto } from "../lib/api";
import { euro, isZero, money, monthName, shortDate } from "../lib/format";
import { ErrorBox, Spinner } from "../components/ui";

interface Half {
  key: string;
  label: string;
  from: string;
  to: string;
}

interface Zeile {
  employeeId: string;
  personalNr: number;
  timeClockId: number | null;
  name: string;
  byDate: Record<string, string>;
  byHalf: Record<string, string>;
  totalAmount: string;
}

interface Overview {
  from: string;
  to: string;
  dates: string[];
  totalsByDate: Record<string, string>;
  total: string;
  halves: Half[];
  totalsByHalf: Record<string, string>;
  rows: Zeile[];
  openDays: string[];
}

export function Aushang() {
  const [params, setParams] = useSearchParams();
  const monat = params.get("monat") ?? "";
  // Anonym ist der Normalfall; die Fassung mit Namen muss man wählen.
  const mitNamen = params.get("namen") === "1";
  const haelfte = params.get("haelfte") === "1" ? 1 : params.get("haelfte") === "2" ? 2 : null;
  // Zurück dorthin, woher man kam.
  const zurueck = params.get("von") === "auszahlungen" ? "/auszahlungen" : "/monat";
  const setze = (key: string, value: string | null) => {
    const p = new URLSearchParams(params);
    if (value === null) p.delete(key);
    else p.set(key, value);
    setParams(p);
  };

  const workflow = useQuery({
    queryKey: ["workflow"],
    queryFn: () => api.get<WorkflowDto>("/workflow"),
  });
  const gewaehlt = monat || workflow.data?.periode?.month || "";
  const bereich = monatsBereich(gewaehlt, haelfte);

  const overview = useQuery({
    queryKey: ["overview", bereich?.from, bereich?.to],
    enabled: bereich !== null,
    queryFn: () => api.get<Overview>(`/overview?from=${bereich?.from}&to=${bereich?.to}`),
  });

  if (!bereich) return <Spinner label="Aushang wird vorbereitet" />;
  if (overview.isLoading) return <Spinner label="Aushang wird geladen" />;
  if (overview.error) return <ErrorBox error={overview.error} />;

  const d = overview.data;
  if (!d || d.rows.length === 0) {
    return (
      <div className="p-8">
        <p className="text-sm text-muted">
          Für {monthName(gewaehlt)} gibt es noch keine berechneten Beträge.
        </p>
        <Link to={zurueck} className="btn-ghost mt-4 inline-flex">
          Zurück
        </Link>
      </div>
    );
  }

  const unfertig = d.openDays.length > 0;
  // Bei einer Hälfte wäre „Gesamt“ nur dieselbe Zahl wie „Summe“ noch einmal.
  const mitGesamt = d.halves.length > 1;
  const halbOffen = (h: Half) => d.openDays.some((x) => x >= h.from && x <= h.to);
  // ID, Tage, je Hälfte eine Summe, beim ganzen Monat dazu „Gesamt“.
  const spalten = 1 + d.dates.length + d.halves.length + (mitGesamt ? 1 : 0);

  return (
    <div className="aushang">
      {/*
        * Sind Tage offen, ist das Blatt eine Vorschau — und das muss man ihm
        * auf jeder gedruckten Seite ansehen, nicht nur in einem Kasten oben.
        * Sperren lässt sich das Drucken nicht (Strg+P geht immer); ein
        * Wasserzeichen über der ganzen Seite schon.
        */}
      {unfertig ? (
        <div className="aushang-wasserzeichen" aria-hidden="true">
          <span className="gross">Vorschau</span>
          <span className="klein">nicht aushängen</span>
        </div>
      ) : null}

      {/* Bedienelemente erscheinen nicht auf dem Papier. */}
      <div className="mb-6 flex flex-wrap items-center gap-3 print:hidden">
        {unfertig ? (
          <button className="btn-outline" onClick={() => window.print()}>
            Vorschau drucken
          </button>
        ) : (
          <button className="btn-recommend" onClick={() => window.print()}>
            Drucken oder als PDF sichern
          </button>
        )}
        <button
          className={mitNamen ? "btn-outline" : "btn-primary"}
          onClick={() => setze("namen", null)}
        >
          Nur Stempeluhr-IDs
        </button>
        <button
          className={mitNamen ? "btn-primary" : "btn-outline"}
          onClick={() => setze("namen", "1")}
        >
          Mit Namen
        </button>
        <span className="mx-1 h-6 border-l border-line" aria-hidden="true" />
        <button className={haelfte === 1 ? "btn-primary" : "btn-outline"} onClick={() => setze("haelfte", "1")}>
          Erste Hälfte
        </button>
        <button className={haelfte === 2 ? "btn-primary" : "btn-outline"} onClick={() => setze("haelfte", "2")}>
          Zweite Hälfte
        </button>
        <button className={haelfte === null ? "btn-primary" : "btn-outline"} onClick={() => setze("haelfte", null)}>
          Ganzer Monat
        </button>
        <Link to={zurueck} className="btn-ghost">
          Zurück
        </Link>
        <span className="text-xs text-muted">
          Im Druckdialog Querformat wählen. Erscheinen Datum und Adresse am Rand, unter „Weitere
          Einstellungen“ das Häkchen „Kopf- und Fußzeilen“ entfernen.
        </span>
      </div>

      {/*
        * Das Blatt selbst: hell wie Papier, auch im Dunkelmodus, damit der
        * Bildschirm zeigt, was aus dem Drucker kommt.
        */}
      <div className="aushang-blatt">
        {/*
          * Die Warnung steht bewusst auch auf dem Papier: ein Ausdruck, der
          * unterwegs endgültig aussieht, hängt sonst am Brett, während die
          * Zahlen sich noch ändern.
          */}
        {unfertig ? (
          <p className="aushang-warnung">
            <strong>Noch nicht endgültig.</strong> {d.openDays.length}{" "}
            {d.openDays.length === 1 ? "Betriebstag ist" : "Betriebstage sind"} noch nicht
            freigegeben. Diese Beträge können sich noch ändern, deshalb nicht aushängen.
          </p>
        ) : null}

        <header className="aushang-kopf">
          <div>
            <div className="aushang-label">
              Bhf. Ehrenfeld · Trinkgeld{mitNamen ? "" : " · nach Stempeluhr-ID"}
            </div>
            <h1 className="aushang-titel">
              {monthName(gewaehlt)}
              {unfertig ? <span className="aushang-vorschau">Vorschau</span> : null}
            </h1>
            <p className="aushang-untertitel">
              {bereich ? zeitraum(bereich, haelfte) : null}
              {/* Für die Buchhaltung, nicht fürs Brett (Rückmeldung Oktober 2026). */}
              <span className="nur-bildschirm">
                {" "}· {d.rows.length} Personen · {d.dates.length}{" "}
                {d.dates.length === 1 ? "Abend" : "Abende"}
              </span>
            </p>
          </div>
          <div className="aushang-kennzahl nur-bildschirm">
            <div className="aushang-label">
              {unfertig ? "Stand jetzt, vorläufig" : haelfte ? "Auszahlung" : "Auszahlungen im Monat"}
            </div>
            <div className="aushang-summe">{euro(d.total)}</div>
          </div>
        </header>

        <table className={`aushang-tabelle ${d.dates.length > 16 ? "viele" : ""}`}>
          <thead>
            {/* Abstand zum Papierrand, auf jeder gedruckten Seite: der Kopf wiederholt sich. */}
            <tr className="druck-rand" aria-hidden="true">
              <th colSpan={spalten} />
            </tr>
            <tr>
              <th className="id">
                <span className="kopf-oben">{mitNamen ? "Name" : "Stempeluhr-ID"}</span>
              </th>
              {d.halves.map((h) => {
                const tage = d.dates.filter((x) => x >= h.from && x <= h.to);
                return (
                  <Spalten key={h.key} tage={tage}>
                    {/* Zwei Spalten „Summe" ohne Unterschied wären in der
                        Vorlage „Summe" und „Summe2" — der Zeitraum sagt mehr. */}
                    <th className="summe">
                      <span className="kopf-oben">Summe</span>
                      <span className="kopf-unten">
                        {tagNr(h.from)}.–{tagNr(h.to)}.
                      </span>
                      {halbOffen(h) ? <span className="kopf-vorlaeufig">vorläufig</span> : null}
                    </th>
                  </Spalten>
                );
              })}
              {mitGesamt ? (
                <th className="summe">
                  <span className="kopf-oben">Gesamt</span>
                  <span className="kopf-unten">Monat</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {[...d.rows]
              .sort((a, b) =>
                mitNamen
                  ? a.name.localeCompare(b.name)
                  : (a.timeClockId ?? 0) - (b.timeClockId ?? 0),
              )
              .map((r) => (
                <tr key={r.employeeId}>
                  <td className="id">
                    {mitNamen ? (
                      <>
                        {r.name}
                        <span className="nebenbei">{r.personalNr}</span>
                      </>
                    ) : (
                      (r.timeClockId ?? "—")
                    )}
                  </td>
                  {d.halves.map((h) => (
                    <Spalten
                      key={h.key}
                      tage={d.dates.filter((x) => x >= h.from && x <= h.to)}
                      werte={r.byDate}
                    >
                      <SummenZelle wert={r.byHalf[h.key]} />
                    </Spalten>
                  ))}
                  {mitGesamt ? <SummenZelle wert={r.totalAmount} /> : null}
                </tr>
              ))}
          </tbody>
          <tfoot>
            {/* Die Tagessummen zeigen das Gesamt-Trinkgeld; das bleibt am Bildschirm. */}
            <tr className="nur-bildschirm">
              <td className="id">Gesamt</td>
              {d.halves.map((h) => (
                <Spalten
                  key={h.key}
                  tage={d.dates.filter((x) => x >= h.from && x <= h.to)}
                  werte={d.totalsByDate}
                >
                  <SummenZelle wert={d.totalsByHalf[h.key]} />
                </Spalten>
              ))}
              {mitGesamt ? <SummenZelle wert={d.total} /> : null}
            </tr>
            <tr className="druck-rand" aria-hidden="true">
              <td colSpan={spalten} />
            </tr>
          </tfoot>
        </table>

        <footer className="aushang-fuss">
          <span>Beträge in Euro. Ein Punkt heißt: an diesem Abend kein Trinkgeld.</span>
          <span>
            Erstellt am {new Date().toLocaleDateString("de-DE")} · Bhf. Ehrenfeld GmbH
            {unfertig ? ` · Vorschau: ${d.openDays.length} Tage noch nicht freigegeben` : ""}
          </span>
        </footer>
      </div>
    </div>
  );
}

/** Die Tagesspalten einer Hälfte, gefolgt von deren Summenspalte. */
function Spalten({
  tage,
  werte,
  children,
}: {
  tage: string[];
  werte?: Record<string, string>;
  children: React.ReactNode;
}) {
  return (
    <>
      {tage.map((t) =>
        werte ? (
          <BetragsZelle key={t} wert={werte[t]} />
        ) : (
          <th key={t}>
            <span className="kopf-oben">{wochentag(t)}</span>
            <span className="kopf-unten">{tagNr(t)}</span>
          </th>
        ),
      )}
      {children}
    </>
  );
}

/**
 * Nichts bekommen heißt ein blasser Punkt, nicht „– €“: dreihundert „– €“ auf
 * einer Seite machen das Blatt grau und die Beträge schwer zu finden.
 */
function BetragsZelle({ wert }: { wert: string | undefined }) {
  return isZero(wert) ? <td className="leer">·</td> : <td>{money(wert ?? "")}</td>;
}

/** Die Summe ist der ausgezahlte Betrag; nur sie trägt das Euro-Zeichen. */
function SummenZelle({ wert }: { wert: string | undefined }) {
  return <td className="summe">{isZero(wert) ? "·" : `${money(wert ?? "")} €`}</td>;
}

/** `2026-09-02` → „Mi“ */
function wochentag(iso: string): string {
  const [y, m, t] = iso.split("-").map(Number) as [number, number, number];
  return ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][new Date(Date.UTC(y, m - 1, t)).getUTCDay()]!;
}

/** `2026-09-02` → „02“ */
function tagNr(iso: string): string {
  return iso.slice(8, 10);
}

/** „01.–15.09. · erste Monatshälfte“ */
function zeitraum(bereich: { from: string; to: string }, haelfte: 1 | 2 | null): string {
  const spanne = `${tagNr(bereich.from)}.–${shortDate(bereich.to)}`;
  if (haelfte === 1) return `${spanne} · erste Monatshälfte`;
  if (haelfte === 2) return `${spanne} · zweite Monatshälfte`;
  return `${spanne} · ganzer Monat`;
}

/** `2026-08` → Monatsanfang und -ende, mit `haelfte` nur 1.–15. oder 16.–Ende. */
function monatsBereich(monat: string, haelfte: 1 | 2 | null): { from: string; to: string } | null {
  if (!/^\d{4}-\d{2}$/.test(monat)) return null;
  const [y, m] = monat.split("-").map(Number) as [number, number];
  const letzter = `${monat}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, "0")}`;
  if (haelfte === 1) return { from: `${monat}-01`, to: `${monat}-15` };
  if (haelfte === 2) return { from: `${monat}-16`, to: letzter };
  return { from: `${monat}-01`, to: letzter };
}

export { euro };
